import './Colorpill.scss';

import React from 'react';
import classNames from 'classnames';
import type { DataAttributes } from '@/components/utils/TypeUtils';
/* eslint-disable react/jsx-no-useless-fragment */
import { ToolTip } from '@/components/Tooltip/Tooltip';
import { getBgColor } from '@/utilities/CommonUtils';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import { returnToolTipTriggerByViewport } from '@/utilities/CommonUtils.misc';
import useViewport from '@/common/context/ViewPort/useViewPort';

interface ColorPillProps {
  /** Size options are mainly to provide variety, and consistency when using color pills for different usages. */
  size: 'extra-small' | 'small' | 'medium';
  /** Color of the pill */
  backgroundColor: string;
  /** Specify an optional className to be added to color pill. */
  className?: string;
  /** Specify an optional color name to be added to color pill as tooltip */
  colorName?: string;
  /** The data-* attribute is used to store custom data private to the page or application. */
  dataAttributes?: DataAttributes;
  /** Whether to show tooltip or not */
  showTooltip?: boolean;
  /** Provide an optional function to be called when the color pill element is clicked. */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether to show title or not */
  showTitle?: boolean;
  /** To show title */
  title?: string;
  type?: 'button' | 'div';
}

/**
 * ColorPill is a compact circular label that appears beside a primary interface area which is used to represent color for that area
 */
const ColorPill: React.FC<ColorPillProps> = ({
  size,
  backgroundColor,
  className,
  colorName,
  onClick,
  dataAttributes,
  showTooltip = true,
  showTitle = false,
  title = '',
  type = 'button',
}) => {
  const screen = useViewport();
  const colorPillClass = classNames('color-pill', `color-pill--${size}`, className);

  const ELEMENT_TYPE = type;

  const ELEMENT_PROPS = {
    className: colorPillClass,
    style: { background: getBgColor(backgroundColor) },
    ...getPrefixedDataAttributes(dataAttributes, 'ColorPill'),
    onClick: (e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLDivElement>) =>
      onClick && onClick(e as React.MouseEvent<HTMLButtonElement>),
    title: showTitle ? title : '',
    type: type === 'button' ? 'button' : undefined,
  };

  return (
    <>
      {showTooltip ? (
        <ToolTip
          title={colorName as string}
          overlayInnerStyle={{ boxShadow: '0px 4px 15px #8588AC26' }}
          trigger={returnToolTipTriggerByViewport(screen)}
        >
          {React.createElement(ELEMENT_TYPE, ELEMENT_PROPS)}
        </ToolTip>
      ) : (
        React.createElement(ELEMENT_TYPE, ELEMENT_PROPS)
      )}
    </>
  );
};

ColorPill.displayName = 'ColorPill';

export default ColorPill;
